import { getAccountTypes } from "apis";
import { useApi, useMount } from "hooks";
import locale from "localization";
import { useMemo } from "react";
import { formatNumber } from "utils";
import { ACCOUNT_TYPE_GROUP_OPTIONS } from "../account-type-vip-details/account-type-vip-details.state";

export const FieldKind = {
  Amount: "amount",
  Volume: "volume",
  VolumeRate: "volumeRate",
  Points: "points",
  Count: "count",
};

export const DECIMAL_PLACES = {
  [FieldKind.Amount]: 2,
  [FieldKind.Volume]: 3,
  [FieldKind.VolumeRate]: 2,
  [FieldKind.Points]: 2,
  [FieldKind.Count]: 0,
};

// Formatting limits: 999,999,999,999 (+ decimals per DECIMAL_PLACES)
export const MAX_INTEGER_DIGITS = 12;
export const MAX_COUNT = Number("9".repeat(MAX_INTEGER_DIGITS));

const { Amount, Volume, VolumeRate, Points, Count } = FieldKind;

export const TIER_ROWS = [
  { spacer: true },
  { label: locale.tierThresholdLiters, fields: [["tierThresholdLiters", Volume]] },

  { section: locale.earningRate },
  { label: locale.fuel, fields: [["fuelEarningLitersPerPoint", VolumeRate]] },
  { label: locale.nonFuelShort, fields: [["nonfuelEarningPesoPerPoint", Amount]] },

  { section: locale.pointsEarningLimits, pairHeader: locale.transactionLimit },
  {
    label: locale.daily,
    strong: true,
    fields: [["pointsEarnDaily", Points], ["pointsEarnTxnCapDaily", Count]],
  },
  {
    label: locale.weekly,
    strong: true,
    fields: [["pointsEarnWeekly", Points], ["pointsEarnTxnCapWeekly", Count]],
  },
  {
    label: locale.monthly,
    strong: true,
    fields: [["pointsEarnMonthly", Points], ["pointsEarnTxnCapMonthly", Count]],
  },

  { section: locale.pointsRedemptionLimitsShort, pairHeader: locale.redemptionLimitHeader },
  {
    label: locale.daily,
    strong: true,
    fields: [["pointsRedeemDaily", Points], ["pointsRedeemCapDaily", Count]],
  },
  {
    label: locale.weekly,
    strong: true,
    fields: [["pointsRedeemWeekly", Points], ["pointsRedeemCapWeekly", Count]],
  },
  {
    label: locale.monthly,
    strong: true,
    fields: [["pointsRedeemMonthly", Points], ["pointsRedeemCapMonthly", Count]],
  },

  { spacer: true },
  { label: locale.pesoWalletSize, fields: [["walletSize", Amount]] },
  { label: locale.fuelTankSize, fields: [["fuelTankSize", Volume]] },

  { section: locale.cashInLimit },
  { label: locale.daily, fields: [["totalCashinLimitDaily", Amount]] },
  { label: locale.monthly, fields: [["totalCashinLimitMonthly", Amount]] },

  { section: locale.buyFuelLimit },
  { label: locale.daily, fields: [["totalBuyFuelLimitDaily", Volume]] },
  { label: locale.monthly, fields: [["totalBuyFuelLimitMonthly", Volume]] },

  { section: locale.payWithPesoLimit },
  { label: locale.daily, fields: [["totalPayWithPesoLimitDaily", Amount]] },
  { label: locale.monthly, fields: [["totalPayWithPesoLimitMonthly", Amount]] },

  {
    label: locale.dailyRedemptionLiterLimit,
    fields: [["totalRedeemLimitDaily", Count], ["totalRedemptionLiterLimitDaily", Volume]],
  },
];

export const TIER_FIELDS = TIER_ROWS.flatMap((row) => row.fields || []);

export const isEmptyValue = (v) => v == null || v === "";

export const toNumber = (v) => (isEmptyValue(v) ? null : Number(String(v).replace(/,/g, "")));

export const formatByKind = (value, kind) => {
  if (isEmptyValue(value)) {
    return "";
  }
  const num = toNumber(value);
  if (kind === Points) {
    return num.toLocaleString("en", { maximumFractionDigits: DECIMAL_PLACES[Points] });
  }
  return formatNumber(num, DECIMAL_PLACES[kind]);
};

export const useAccountTypeGroupTiers = (accountTypeId) => {
  const {
    request,
    loading,
    result = { accountTypes: [] },
  } = useApi({
    api: getAccountTypes,
    pageError: true,
  });

  useMount(() => {
    request({ page: 1, perPage: 200 });
  });

  const group = useMemo(() => {
    const { accountTypes = [] } = result;
    const selected = accountTypes.find((a) => a.accountTypeId === accountTypeId);
    if (!selected) {
      return { groupKey: null, groupLabel: "", tiers: [] };
    }
    if (!selected.accountTypeGroup) {
      return { groupKey: null, groupLabel: selected.name, tiers: [selected] };
    }
    const option = ACCOUNT_TYPE_GROUP_OPTIONS.find((o) => o.value === selected.accountTypeGroup);
    return {
      groupKey: selected.accountTypeGroup,
      groupLabel: selected.accountTypeGroupName || option?.label || selected.accountTypeGroup,
      tiers: accountTypes
        .filter((a) => a.accountTypeGroup === selected.accountTypeGroup)
        .sort((x, y) => (x.tierLevel ?? 0) - (y.tierLevel ?? 0)),
    };
  }, [result, accountTypeId]);

  return { loading, accountTypes: result.accountTypes || [], ...group };
};
